import React from 'react';

import { CandidateDetailsAction } from '@constants';
import { IAvailableCandidatesViewState } from '@hooks';
import { IAvailableCandidate } from '@types';
import { RankingValue } from 'components/common/RankingValue/RankingValue';
import { ScoreCell } from 'components/common/ScoreCell/ScoreCell';
import { GroupedRows } from 'components/candidate/CandidatesList/grouping';
import {
  GroupedTable,
  useResolvedColumns,
} from 'components/candidate/CandidatesList/components/GroupedTable';
import { AvailableCandidateActions } from './AvailableCandidateActions';
import { AvailableCandidateMaterials } from './AvailableCandidateMaterials';
import { AvailableCandidateNameCell } from './AvailableCandidateNameCell';
import { AvailableFromCell } from './AvailableFromCell';
import {
  AvailableCandidateColumns,
  IAvailableCandidateColumn,
} from './columns';

interface IAvailableCandidatesTableProps {
  groups: GroupedRows<IAvailableCandidate>[];
  view: IAvailableCandidatesViewState;
  onSort: (key: string) => void;
  onCandidateClick: (id: string) => void;
  onAction: (candidateId: string, action: CandidateDetailsAction) => void;
  onLearnMore: (candidateId: string) => void;
  onOpenResume: (candidateId: string) => void;
}

export const AvailableCandidatesTable: React.FC<
  IAvailableCandidatesTableProps
> = ({
  groups,
  view,
  onSort,
  onCandidateClick,
  onAction,
  onLearnMore,
  onOpenResume,
}) => {
  const { columns } = useResolvedColumns(
    AvailableCandidateColumns,
    view.columns,
  );

  const renderCell = (
    column: IAvailableCandidateColumn,
    candidate: IAvailableCandidate,
  ) => {
    switch (column.id) {
      case 'candidate':
        return <AvailableCandidateNameCell candidate={candidate} />;
      case 'technicalRanking':
        return (
          <RankingValue value={candidate.ranking?.technicalInterview ?? null} />
        );
      case 'softSkills':
        return <RankingValue value={candidate.ranking?.softSkills ?? null} />;
      case 'tt':
        return (
          <ScoreCell
            testTask={{
              score: candidate.testTask.score,
              status: null,
              isPending: false,
            }}
            label="test task"
          />
        );
      case 'materials':
        return (
          <AvailableCandidateMaterials
            candidate={candidate}
            onPlay={() => onCandidateClick(candidate.id)}
            onOpenResume={onOpenResume}
          />
        );
      case 'availableFrom':
        return <AvailableFromCell candidate={candidate} />;
      case 'nextSteps':
        return (
          <AvailableCandidateActions
            candidate={candidate}
            variant="row"
            onAction={onAction}
            onLearnMore={onLearnMore}
          />
        );
      default:
        return null;
    }
  };

  return (
    <GroupedTable
      groups={groups}
      columns={columns}
      sort={view.sort}
      onSort={onSort}
      onRowClick={onCandidateClick}
      renderCell={renderCell}
    />
  );
};
